// A10.3 — Détection des capacités par véhicule, UNIQUEMENT à partir des données déjà
// chargées via GET /vehicles (last_position.can/io/external_voltage, dtc_*, sos_enabled,
// champs conducteur). Aucun appel réseau supplémentaire, aucune boucle par véhicule.
// Principe : on ne considère une capacité présente que si elle est RÉELLEMENT observée
// ou configurée — jamais déduite du modèle de boîtier (cf. §13 : pas de faux positif).

const CAN_KEYS = ["rpm", "coolant_temp", "fuel_pct", "throttle", "can_speed", "fuel_used_l", "total_mileage_km", "fuel_l"];
const IO_DTC_COUNT = "30"; // Compteur DTC (adaptateur CAN)

const num = (x) => typeof x === "number" && Number.isFinite(x);
const lp = (v) => v?.last_position || {};
const io = (v) => lp(v).io || {};
const can = (v) => lp(v).can || {};

// CAN réellement reçu : au moins une valeur bus décodée non nulle dans la dernière trame.
export const hasCan = (v) => CAN_KEYS.some((k) => can(v)[k] != null);

// Tension véhicule réellement mesurée (external_voltage en mV).
export const hasBattery = (v) => num(lp(v).external_voltage) && lp(v).external_voltage > 0;

// DTC réellement disponible/observé : codes/compteur présents ou historiquement présents,
// ou canal IO30 (compteur DTC) effectivement remonté par le boîtier.
export const hasDtc = (v) => !!v?.dtc_at || (v?.dtc_count || 0) > 0 || (v?.dtc_codes?.length || 0) > 0 || io(v)[IO_DTC_COUNT] != null;

// Niveau carburant exploitable : fuel_pct plausible [0..100] (qualification minimale A7A/A7B côté point).
export const hasFuel = (v) => {
  const fp = can(v).fuel_pct != null ? can(v).fuel_pct : io(v)["89"];
  return num(fp) && fp >= 0 && fp <= 100;
};

// Identification conducteur — CAPABILITY RÉELLE (alignée backend has_driver_identification).
// TRUE uniquement si le véhicule est réellement configuré/équipé : champ télématique conducteur
// mappé (driver_id_field) OU lecteur Dallas/RFID prouvé par une lecture réelle (driver_id_equipped
// persisté à l'ingestion). JAMAIS la simple présence d'un IO (valeur 0), ni « conducteur affecté ».
export const hasDriver = (v) => !!v?.driver_id_field || v?.driver_id_equipped === true;

// SOS : entrée SOS déclarée activée sur le véhicule.
export const hasSos = (v) => v?.sos_enabled === true;

export function vehicleCaps(v) {
  return { battery: hasBattery(v), can: hasCan(v), dtc: hasDtc(v), fuel: hasFuel(v), driver: hasDriver(v), sos: hasSos(v) };
}

// Véhicules d'un périmètre — filtrés depuis la liste DÉJÀ chargée (aucune requête).
export function vehiclesInScope(vehicles, scopeKind, scopeId, pools) {
  if (!scopeKind || scopeKind === "fleet") return vehicles;
  if (scopeKind === "company") return vehicles.filter((v) => v.company_id === scopeId);
  if (scopeKind === "pool") {
    const p = (pools || []).find((x) => x.id === scopeId);
    const ids = new Set(p?.vehicle_ids || []);
    return vehicles.filter((v) => ids.has(v.id));
  }
  if (scopeKind === "vehicle") return vehicles.filter((v) => v.id === scopeId);
  return vehicles;
}

export function countCaps(vehicles) {
  const c = { battery: 0, can: 0, dtc: 0, fuel: 0, driver: 0, sos: 0, total: (vehicles || []).length };
  (vehicles || []).forEach((v) => {
    if (hasBattery(v)) c.battery++;
    if (hasCan(v)) c.can++;
    if (hasDtc(v)) c.dtc++;
    if (hasFuel(v)) c.fuel++;
    if (hasDriver(v)) c.driver++;
    if (hasSos(v)) c.sos++;
  });
  return c;
}
